import { auth as authConfig, buildAuth, setAuthConfig } from './auth';
import { getLocalPermissions } from './localPermissions';
import uniq from 'lodash/uniq';
import { ACTIVE_THEME_NAME } from '../../branding';
import { translationService } from '../translationService'; 

/**
 * A utility class used to assist in the manipulation or accessing of a user's
 * authentication state.
 */
export class AuthService {

    _SIGN_IN_REDIRECT_PATH_STORAGE_KEY = 'signInRedirectPath';

    _auth = null;
    _customAuthState = undefined;

    constructor( auth ) {
        this._auth = auth;
    }

    /**
     * Returns the user's access token if they are logged in. If the user is not
     * logged in, `undefined` will be returned instead.
     * 
     * @returns { string }
     */
    getAccessToken() {
        return this._auth?.state?.access_token;
    }

    /**
     * Returns whether or not the user is currently authorized and able to view
     * pages available only to authorized users.
     * 
     * @returns { boolean }
     */
    isAuthenticated() {
        return Boolean( this.getAccessToken() );
    }

    /**
     * The permissions that the user has assuming there is a valid auth state
     * available. The auth state data is used to help determine what type of
     * user the user is and what permissions they have.
     * 
     * @type { string[] }
     */
    getPermissions() {
        const statePermissions = this._auth?.state?.claims?.permissions?.[ 'hoya-learning-center' ] || [];
        const localPermissions = getLocalPermissions( this._auth?.state?.claims?.person_id );

        // Merge any permissions pulled from the user's claims and any permissions
        // found locally. I realize that doing this every time this function is called
        // isn't super efficient, but it works for now if we want to support local
        // permissions.
        const permissions = uniq( [
            ...statePermissions,
            ...localPermissions,
        ] );

        if ( !permissions ) {
            return [];
        } else {
            return permissions;
        }
    }

    getAttributes() {
        const stateAttributes = this._auth?.state?.claims?.attributes || {};
         
        // Comment this out to test 
        // stateAttributes[ 'isseikoacademyuser' ] = 'true';
        // stateAttributes[ 'ishoyalearningcenteruser' ] = 'true';
        
        return stateAttributes; 
    }

    isAdminUser() {
        const noSegmentationAdminUsers = [
            '9d63742d-c9f3-4140-a146-27bb27062aab', // Joe Buck QA
            'a31f31f9-2af6-49d4-a419-3698d04426ee', // Joe Buck PPRD
            '3bf47e43-7c3a-404a-822a-e83283552b15', // Rise.SCORMTest QA
            // Add Gary's PPRD and PROD users here
            '6d1bc078-23cd-46fc-b4f8-41a41b1a09d0', // Rise.SCORMTest' PPRD,
            '3c63faac-4558-4797-acc4-9a336bdb6cc2'  // Gary's PROD user, SuperUser.HLC
        ];

        const personId = this._auth?.state?.claims?.[ 'person_id' ];
        return noSegmentationAdminUsers.includes( personId );
    }
    
    getUserId() {
        return this._auth?.state?.claims?.[ 'person_id' ] || 'No user id';
    }

    getUserEmail() {
        return this._auth?.state?.claims?.[ 'email' ] || 'No user email';
    }

    getName() {
        return this._auth?.state?.claims?.[ 'name' ] || 'No user name';
    }

    /**
     * The tags given to the user by the webapp. These may be built using data
     * from the user's attributes.
     * 
     * @returns { string[] }
     */
    getTags() {
        const attributes = authService.getAttributes();
        
        const propertyTags = [
            { key: 'is_portal_user', evaluate: ( auth ) => attributes?.[ 'is_portal_user' ] !== 'false' },
            { key: 'ishoyalearningcenteruser', evaluate: ( auth ) => attributes?.[ 'ishoyalearningcenteruser' ] !== 'false' },
            { key: 'isseikoacademyuser', evaluate: ( auth ) => attributes?.[ 'isseikoacademyuser' ] !== 'false' },
            { key: 'miyosmart', evaluate: ( auth ) => attributes?.[ 'ismiyosmartuser' ] !== 'false' },
            { key: 'employee', evaluate: ( auth ) => attributes?.[ 'relationshipToCompany' ] === 'employee' },
            { key: 'customer', evaluate: ( auth ) => attributes?.[ 'relationshipToCompany' ] === 'customer' },
            { key: attributes?.[ 'lmsLearningProfile' ], evaluate: _ => !!attributes?.[ 'lmsLearningProfile' ] },
        ];

        const userTags = propertyTags
            .filter( ( tag ) => ( tag.evaluate( this._auth ) ) )
            .map( ( tag ) => tag?.key )
            .filter( tagKey => Boolean( tagKey ) );

        return userTags;
    }

    getClaims() {
        return this._auth?.state?.claims;
    }

    async signInToSalesforce() {
        // Recreate the auth controller to redirect to Salesforce.
        this._setHasSession( undefined );
        const newConfig = buildAuth( { source: 'salesforce', theme: ACTIVE_THEME_NAME, 
            locale: translationService.getLocale() } );
        setAuthConfig( newConfig );
        this._auth = newConfig;
        this.checkSession();
        
        return this.signIn();
    }

    async signInToAzure() {
        // Recreate the auth controller to redirect to Azure.
        this._setHasSession( undefined );
        const newConfig = buildAuth( { source: 'azure', theme: ACTIVE_THEME_NAME, 
            locale: translationService.getLocale() } );
        setAuthConfig( newConfig );
        this._auth = newConfig;
        this.checkSession();

        return this.signIn();
    }

    async signIn() {
        if ( !this.isAuthenticated() ) {
            await this._auth.signIn();
        }
    }

    async signOut() {
        if ( this.isAuthenticated() ) {
            await this._auth.signOut();
        }
    }

    async renew() {
        await this._auth.renew();
    }

}

export const authService = new AuthService( authConfig );
